import { type AskPunttSearchProgress } from "@mg/schemas/src/christo/askPuntt";
import {
  Buildings,
  CaretDown,
  CircleNotch,
  ClockCounterClockwise,
  File,
  Globe,
  MagnifyingGlass,
  Ticket,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";

import { TOOL_CHIP_CLASS } from "./AgentSteps";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

interface SearchProgressProps {
  searchProgress: AskPunttSearchProgress[];
}

function SourceIcon({ source }: { source: string }) {
  const s = source.toLowerCase();
  const cls = "shrink-0";
  if (s.includes("enterprise"))
    return (
      <Buildings
        size={14}
        className={`${cls} text-puntt-accent-9`}
        aria-hidden
      />
    );
  if (s.includes("ticket"))
    return (
      <Ticket size={14} className={`${cls} text-puntt-pink-9`} aria-hidden />
    );
  if (s.includes("review history"))
    return (
      <ClockCounterClockwise
        size={14}
        className={`${cls} text-puntt-gold-11`}
        aria-hidden
      />
    );
  if (s.includes("regulatory") || s.includes("puntt"))
    return (
      <MagnifyingGlass
        size={14}
        className={`${cls} text-puntt-accent-9`}
        aria-hidden
      />
    );
  if (s.includes("sharepoint") || s.includes("claims"))
    return (
      <File size={14} className={`${cls} text-puntt-gold-9`} aria-hidden />
    );
  if (s.includes("code"))
    return (
      <File size={14} className={`${cls} text-puntt-accent-11`} aria-hidden />
    );
  return (
    <Globe size={14} className={`${cls} text-malachite-700`} aria-hidden />
  );
}

function SourceRow({ source }: { source: AskPunttSearchProgress }) {
  const isSearching = source.status === "searching";
  const isError = source.status === "error";
  const resultCount =
    source.resultCount ??
    (source.results?.length != null ? source.results.length : undefined);

  return (
    <Flex className={TOOL_CHIP_CLASS}>
      {isSearching ? (
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
      ) : (
        <SourceIcon source={source.source} />
      )}
      <Text
        size="1"
        weight="medium"
        className={`flex-1 ${
          isSearching || isError
            ? "text-puntt-neutral-gray-11"
            : "text-puntt-neutral-gray-12"
        }`}
      >
        {isSearching ? "Searching " : ""}
        {source.source}
      </Text>
      {isError ? (
        <Text size="1" className="text-puntt-red-11">
          Error
        </Text>
      ) : (
        !isSearching &&
        resultCount !== undefined && (
          <Text size="1" className="text-puntt-neutral-gray-9">
            {resultCount} {resultCount === 1 ? "Result" : "Results"}
          </Text>
        )
      )}
    </Flex>
  );
}

export function SearchProgress({ searchProgress }: SearchProgressProps) {
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  if (searchProgress.length === 0) return null;

  const isActive = searchProgress.some((s) => s.status === "searching");
  const expanded = manualToggle !== null ? manualToggle : isActive;

  const summaryText = isActive
    ? "Searching..."
    : `Searched ${searchProgress.length} source${searchProgress.length === 1 ? "" : "s"}`;

  return (
    <div className="my-3 rounded-xl shadow-sm">
      <div
        data-testid="ask-puntt-search-progress"
        className={cx(
          "overflow-hidden",
          interactiveSurfaceClasses({ expandable: true }),
        )}
      >
        <button
          data-testid="search-progress-toggle"
          type="button"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} search progress`}
          onClick={() => setManualToggle(!expanded)}
          className="flex w-full items-center gap-2 bg-base-white px-2.5 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7"
        >
          {isActive ? (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <MagnifyingGlass
              size={16}
              className="shrink-0 text-puntt-accent-9"
              aria-hidden
            />
          )}
          <Text size="1" weight="medium" className="text-puntt-neutral-gray-12">
            {summaryText}
          </Text>
          <CaretDown
            size={14}
            aria-hidden
            className={`ml-auto shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
          />
        </button>

        {expanded && searchProgress.length > 0 && (
          <div
            data-testid="search-progress-items"
            className="border-t border-puntt-neutral-gray-6 px-2.5 py-1.5"
          >
            <div className="flex flex-col gap-1.5">
              {searchProgress.map((source, idx) => (
                <SourceRow key={`${source.source}-${idx}`} source={source} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
